import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Package, 
    FileText, 
    Mail, 
    Layout, 
    LogOut, 
    Plus, 
    Pencil, 
    Trash2,
    Star,
    ShoppingCart,
    Repeat,
    X,
    Tag,
    Truck,
    AlertTriangle,
    Check,
    Clock,
    ChevronDown,
    Upload,
    ExternalLink,
    Activity,
    Calendar,
    DollarSign,
    BarChart3,
    Users,
    Search,
    Shield
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { API, withBackendOrigin, normalizeRenderableImageUrl } from '../lib/api';

const LOGO_URL = "/images/wayside-logo-horizontal.png";

const AdminDashboard = () => {
    const { logout, getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('orders');
    const [products, setProducts] = useState([]);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [orders, setOrders] = useState([]);
    const [promoCodes, setPromoCodes] = useState([]);
    const [fulfillments, setFulfillments] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [orderStats, setOrderStats] = useState(null);
    const [orderActivity, setOrderActivity] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [newMemberForm, setNewMemberForm] = useState({ username: '', password: '' });
    const [wholesaleCustomers, setWholesaleCustomers] = useState([]);
    const [wholesaleCustomerForm, setWholesaleCustomerForm] = useState({
        id: null,
        full_name: '',
        email: '',
        business_name: '',
        notes: '',
        default_password: '',
        is_active: true,
    });
    const [loading, setLoading] = useState(true);
    const [shippingSettings, setShippingSettings] = useState({
        enabled: true,
        mode: 'flat_rate',
        flat_rate: '8.00',
        free_over: '75.00',
        label: 'Standard Shipping',
        free_label: 'Free Shipping',
        allowed_countries: 'US',
        subscription_free_shipping: true,
    });

    // Filters
    const [orderStatusFilter, setOrderStatusFilter] = useState('placed');
    const [orderFulfillmentFilter, setOrderFulfillmentFilter] = useState('all');
    const [orderCustomerTypeFilter, setOrderCustomerTypeFilter] = useState('all');
    const [fulfillmentFilter, setFulfillmentFilter] = useState('pending');

    // Product Modal states
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productForm, setProductForm] = useState({
        name: '', description: '', price: '', compare_at_price: '', category: '',
        images: [], main_image: '',
        featured: false, in_stock: true, sku: '',
        size_value: '', size_unit: '',
        related_product_ids: [], shipping_cost: '', grade_options_text: '', default_grade: 'Dark Robust',
        track_inventory: false, stock_quantity: '0', low_stock_threshold: '10',
        wholesale_enabled: false, wholesale_price: '', wholesale_batch_size: '', wholesale_unit_label: '', wholesale_category: ''
    });
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const res = await axios.post(`${API}/upload/image`, formData, {
                    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
                });
                uploaded.push(res.data.url);
            }
            const newImages = [...productForm.images, ...uploaded];
            const newMain = productForm.main_image || uploaded[0];
            setProductForm({ ...productForm, images: newImages, main_image: newMain });
        } catch (error) {
            alert(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    // Subscription Plan Modal states
    const [isSubPlanModalOpen, setIsSubPlanModalOpen] = useState(false);

    // Subscription image upload
    const handleSubImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/upload/image`, formData, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            setSubPlanForm({ ...subPlanForm, homepage_image_url: res.data.url });
        } catch (error) {
            alert(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };
    const [editingSubPlan, setEditingSubPlan] = useState(null);
    const [subPlanForm, setSubPlanForm] = useState({
        name: '', description: '', price: '', original_price: '',
        interval_months: '3', is_active: true, is_popular: false, is_featured: false,
        features: '', image: '', items: [],
        homepage_subtitle: '', homepage_headline: '', homepage_description: '',
        homepage_features: [{ title: '', description: '' }, { title: '', description: '' }, { title: '', description: '' }],
        homepage_cta_text: 'Start Subscription', homepage_cta_link: '/shop',
        homepage_image_url: '', homepage_badge_text: '', homepage_badge_description: '', homepage_visible: true
    });
    const [availableProducts, setAvailableProducts] = useState([]);

    // Promo Code Modal states
    const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);
    const [promoForm, setPromoForm] = useState({
        code: '', description: '', discount_type: 'percentage', discount_value: '',
        min_order_amount: '', max_uses: '', is_active: true, expires_at: '', applies_to: 'all'
    });

    // Order Detail Modal
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

    const tabs = [
        { id: 'orders', label: 'Orders', icon: ShoppingCart },
        { id: 'fulfillments', label: 'Subscriptions Queue', icon: Truck },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'subscriptions', label: 'Subscription Plans', icon: Repeat },
        { id: 'promos', label: 'Promo Codes', icon: Tag },
        { id: 'customers', label: 'Customers', icon: Users },
        { id: 'wholesaleCustomers', label: 'Wholesale Customers', icon: Users },
        { id: 'team', label: 'Team', icon: Shield },
        { id: 'inventory', label: 'Inventory', icon: AlertTriangle },
        { id: 'shipping', label: 'Shipping', icon: Truck },
        { id: 'contacts', label: 'Messages', icon: Mail },
    ];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = getAuthHeaders();
            switch (activeTab) {
                case 'orders':
                    const params = new URLSearchParams();
                    if (orderStatusFilter === 'all') params.set('status', 'all');
                    else if (orderStatusFilter !== 'placed') params.set('status', orderStatusFilter);
                    if (orderFulfillmentFilter !== 'all') params.set('fulfillment_status', orderFulfillmentFilter);
                    if (orderCustomerTypeFilter !== 'all') params.set('customer_type', orderCustomerTypeFilter);
                    const orderQuery = params.toString() ? `?${params.toString()}` : '';
                    const [ordRes, statsRes] = await Promise.all([
                        axios.get(`${API}/orders${orderQuery}`, { headers }),
                        axios.get(`${API}/orders/stats/summary`, { headers })
                    ]);
                    setOrders(ordRes.data);
                    setOrderStats(statsRes.data);
                    break;
                case 'fulfillments':
                    const fulfQuery = fulfillmentFilter !== 'all' ? `?status=${fulfillmentFilter}` : '';
                    const fulfRes = await axios.get(`${API}/subscription-fulfillments${fulfQuery}`, { headers });
                    setFulfillments(fulfRes.data);
                    break;
                case 'products':
                    const prodRes = await axios.get(`${API}/products`, { headers });
                    setProducts(prodRes.data);
                    break;
                case 'subscriptions':
                    const [subRes, prodForSub] = await Promise.all([
                        axios.get(`${API}/subscription-plans?active_only=false`, { headers }),
                        axios.get(`${API}/products`, { headers })
                    ]);
                    setSubscriptionPlans(subRes.data);
                    setAvailableProducts(prodForSub.data);
                    break;
                case 'promos':
                    const promoRes = await axios.get(`${API}/promo-codes`, { headers });
                    setPromoCodes(promoRes.data);
                    break;
                case 'inventory':
                    const [allProds, lowStock] = await Promise.all([
                        axios.get(`${API}/products`, { headers }),
                        axios.get(`${API}/inventory/low-stock`, { headers })
                    ]);
                    setProducts(allProds.data);
                    setLowStockProducts(lowStock.data);
                    break;
                case 'contacts':
                    const contRes = await axios.get(`${API}/contact/submissions`, { headers });
                    setContacts(contRes.data);
                    break;
                case 'shipping':
                    const shipRes = await axios.get(`${API}/admin/shipping-settings`, { headers });
                    setShippingSettings({
                        enabled: shipRes.data.enabled ?? true,
                        mode: shipRes.data.mode || 'flat_rate',
                        flat_rate: String(shipRes.data.flat_rate ?? '8.00'),
                        free_over: String(shipRes.data.free_over ?? '75.00'),
                        label: shipRes.data.label || 'Standard Shipping',
                        free_label: shipRes.data.free_label || 'Free Shipping',
                        allowed_countries: (shipRes.data.allowed_countries || ['US']).join(', '),
                        subscription_free_shipping: shipRes.data.subscription_free_shipping ?? true,
                    });
                    break;
                case 'customers':
                    const searchParam = customerSearch ? `?search=${encodeURIComponent(customerSearch)}` : '';
                    const custRes = await axios.get(`${API}/customers${searchParam}`, { headers });
                    setCustomers(custRes.data);
                    break;
                case 'team':
                    const teamRes = await axios.get(`${API}/admin/team`, { headers });
                    setTeamMembers(teamRes.data);
                    break;
                case 'wholesaleCustomers':
                    const wholesaleRes = await axios.get(`${API}/admin/wholesale-customers`, { headers });
                    setWholesaleCustomers(wholesaleRes.data);
                    break;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, orderStatusFilter, orderFulfillmentFilter, orderCustomerTypeFilter, fulfillmentFilter, customerSearch, getAuthHeaders]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleLogout = () => { logout(); navigate('/admin'); };

    const saveShippingSettings = async () => {
        try {
            const payload = {
                enabled: !!shippingSettings.enabled,
                mode: shippingSettings.mode,
                flat_rate: Number(shippingSettings.flat_rate || 0),
                free_over: Number(shippingSettings.free_over || 0),
                label: shippingSettings.label,
                free_label: shippingSettings.free_label,
                allowed_countries: shippingSettings.allowed_countries.split(',').map(c => c.trim().toUpperCase()).filter(Boolean),
                subscription_free_shipping: !!shippingSettings.subscription_free_shipping,
            };
            const response = await axios.put(`${API}/admin/shipping-settings`, payload, { headers: getAuthHeaders() });
            setShippingSettings({
                enabled: response.data.enabled ?? true,
                mode: response.data.mode || 'flat_rate',
                flat_rate: String(response.data.flat_rate ?? '8.00'),
                free_over: String(response.data.free_over ?? '75.00'),
                label: response.data.label || 'Standard Shipping',
                free_label: response.data.free_label || 'Free Shipping',
                allowed_countries: (response.data.allowed_countries || ['US']).join(', '),
                subscription_free_shipping: response.data.subscription_free_shipping ?? true,
            });
            alert('Shipping settings saved');
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to save shipping settings');
        }
    };

    // === PRODUCT CRUD ===
    const openProductModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setProductForm({
                name: product.name, description: product.description,
                price: product.price.toString(), compare_at_price: product.compare_at_price?.toString() || '',
                category: product.category,
                images: product.images || [], main_image: product.main_image || '',
                featured: product.featured,
                in_stock: product.in_stock, sku: product.sku || '',
                size_value: product.size_value?.toString() || '', size_unit: product.size_unit || '',
                related_product_ids: product.related_product_ids || [],
                shipping_cost: product.shipping_cost?.toString() || '',
                grade_options_text: Array.isArray(product.grade_options) ? product.grade_options.join('\n') : '',
                default_grade: product.default_grade || 'Dark Robust',
                track_inventory: product.track_inventory || false,
                stock_quantity: (product.stock_quantity || 0).toString(),
                low_stock_threshold: (product.low_stock_threshold || 10).toString(),
                wholesale_enabled: product.wholesale_enabled || false,
                wholesale_price: product.wholesale_price?.toString() || '',
                wholesale_batch_size: product.wholesale_batch_size?.toString() || '',
                wholesale_unit_label: product.wholesale_unit_label || '',
                wholesale_category: product.wholesale_category || ''
            });
        } else {
            setEditingProduct(null);
            setProductForm({
                name: '', description: '', price: '', compare_at_price: '', category: '',
                images: [], main_image: '',
                featured: false, in_stock: true, sku: '',
                size_value: '', size_unit: '',
                related_product_ids: [], shipping_cost: '', grade_options_text: '', default_grade: 'Dark Robust',
                track_inventory: false, stock_quantity: '0', low_stock_threshold: '10',
                wholesale_enabled: false, wholesale_price: '', wholesale_batch_size: '', wholesale_unit_label: '', wholesale_category: ''
            });
        }
        setIsProductModalOpen(true);
    };

    const handleProductSubmit = async (e) => {
        e.preventDefault();
        const headers = getAuthHeaders();
        const data = {
            ...productForm,
            price: parseFloat(productForm.price),
            compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null,
            images: productForm.images,
            main_image: productForm.main_image || (productForm.images.length > 0 ? productForm.images[0] : null),
            size_value: productForm.size_value ? parseFloat(productForm.size_value) : null,
            size_unit: productForm.size_unit || null,
            shipping_cost: productForm.shipping_cost ? parseFloat(productForm.shipping_cost) : null,
            grade_options: productForm.grade_options_text ? productForm.grade_options_text.split('\n').map(g => g.trim()).filter(Boolean) : [],
            default_grade: productForm.grade_options_text ? (productForm.default_grade || 'Dark Robust') : null,
            stock_quantity: parseInt(productForm.stock_quantity) || 0,
            low_stock_threshold: parseInt(productForm.low_stock_threshold) || 10,
            wholesale_price: productForm.wholesale_price ? parseFloat(productForm.wholesale_price) : null,
            wholesale_batch_size: productForm.wholesale_batch_size ? parseInt(productForm.wholesale_batch_size) : null,
            wholesale_unit_label: productForm.wholesale_unit_label || null,
            wholesale_category: productForm.wholesale_category || null
        };

        if (data.wholesale_enabled && (data.wholesale_price == null || data.wholesale_batch_size == null)) {
            alert('Wholesale-enabled products require both a wholesale price and batch size.');
            return;
        }
        try {
            if (editingProduct) {
                await axios.put(`${API}/products/${editingProduct.id}`, data, { headers });
            } else {
                await axios.post(`${API}/products`, data, { headers });
            }
            setIsProductModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Failed to save product');
        }
    };

    const deleteProduct = async (productId) => {
        if (!window.confirm('Delete this product?')) return;
        try {
            await axios.delete(`${API}/products/${productId}`, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const resetWholesaleCustomerForm = () => {
        setWholesaleCustomerForm({
            id: null,
            full_name: '',
            email: '',
            business_name: '',
            notes: '',
            default_password: '',
            is_active: true,
        });
    };

    const editWholesaleCustomer = (customer) => {
        setWholesaleCustomerForm({
            id: customer.id,
            full_name: customer.full_name || '',
            email: customer.email || '',
            business_name: customer.business_name || '',
            notes: customer.notes || '',
            default_password: '',
            is_active: customer.is_active !== false,
        });
    };

    const saveWholesaleCustomer = async (e) => {
        e.preventDefault();
        try {
            if (wholesaleCustomerForm.id) {
                await axios.put(`${API}/admin/wholesale-customers/${wholesaleCustomerForm.id}`, {
                    full_name: wholesaleCustomerForm.full_name,
                    email: wholesaleCustomerForm.email,
                    business_name: wholesaleCustomerForm.business_name || null,
                    notes: wholesaleCustomerForm.notes || null,
                    is_active: wholesaleCustomerForm.is_active,
                }, { headers: getAuthHeaders() });
            } else {
                await axios.post(`${API}/admin/wholesale-customers`, {
                    full_name: wholesaleCustomerForm.full_name,
                    email: wholesaleCustomerForm.email,
                    business_name: wholesaleCustomerForm.business_name || null,
                    notes: wholesaleCustomerForm.notes || null,
                    is_active: wholesaleCustomerForm.is_active,
                    default_password: wholesaleCustomerForm.default_password,
                }, { headers: getAuthHeaders() });
            }
            resetWholesaleCustomerForm();
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to save wholesale customer');
        }
    };

    const resetWholesalePassword = async (customerId) => {
        const nextPassword = prompt('Enter new default password');
        if (!nextPassword) return;
        try {
            await axios.post(`${API}/admin/wholesale-customers/${customerId}/reset-password`, {
                default_password: nextPassword,
            }, { headers: getAuthHeaders() });
            alert('Password reset successfully');
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to reset password');
        }
    };

    const toggleFeatured = async (product) => {
        try {
            await axios.put(`${API}/products/${product.id}`, { featured: !product.featured }, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // === SUBSCRIPTION PLAN CRUD ===
    const openSubPlanModal = async (plan = null) => {
        try {
            const res = await axios.get(`${API}/products`, { headers: getAuthHeaders() });
            setAvailableProducts(res.data);
        } catch (e) {}
        if (plan) {
            const hpf = plan.homepage_features || [];
            while (hpf.length < 3) hpf.push({ title: '', description: '' });
            setEditingSubPlan(plan);
            setSubPlanForm({
                name: plan.name, description: plan.description,
                price: plan.price.toString(), original_price: plan.original_price?.toString() || '',
                interval_months: plan.interval_months?.toString() || '3',
                is_active: plan.is_active, is_popular: plan.is_popular, is_featured: plan.is_featured || false,
                features: plan.features?.join('\n') || '', image: plan.image || '', items: plan.items || [],
                homepage_subtitle: plan.homepage_subtitle || '', homepage_headline: plan.homepage_headline || '',
                homepage_description: plan.homepage_description || '',
                homepage_features: hpf,
                homepage_cta_text: plan.homepage_cta_text || 'Start Subscription',
                homepage_cta_link: plan.homepage_cta_link || '/shop',
                homepage_image_url: plan.homepage_image_url || '',
                homepage_badge_text: plan.homepage_badge_text || '',
                homepage_badge_description: plan.homepage_badge_description || '',
                homepage_visible: plan.homepage_visible !== false
            });
        } else {
            setEditingSubPlan(null);
            setSubPlanForm({
                name: '', description: '', price: '', original_price: '',
                interval_months: '3', is_active: true, is_popular: false, is_featured: false,
                features: '', image: '', items: [],
                homepage_subtitle: '', homepage_headline: '', homepage_description: '',
                homepage_features: [{ title: '', description: '' }, { title: '', description: '' }, { title: '', description: '' }],
                homepage_cta_text: 'Start Subscription', homepage_cta_link: '/shop',
                homepage_image_url: '', homepage_badge_text: '', homepage_badge_description: '', homepage_visible: true
            });
        }
        setIsSubPlanModalOpen(true);
    };

    const handleSubPlanSubmit = async (e) => {
        e.preventDefault();
        const headers = getAuthHeaders();
        const data = {
            name: subPlanForm.name, description: subPlanForm.description,
            price: parseFloat(subPlanForm.price),
            original_price: subPlanForm.original_price ? parseFloat(subPlanForm.original_price) : null,
            interval_months: parseInt(subPlanForm.interval_months),
            is_active: subPlanForm.is_active, is_popular: subPlanForm.is_popular, is_featured: subPlanForm.is_featured,
            features: subPlanForm.features.split('\n').map(s => s.trim()).filter(Boolean),
            image: subPlanForm.image || null, items: subPlanForm.items,
            homepage_subtitle: subPlanForm.homepage_subtitle || null,
            homepage_headline: subPlanForm.homepage_headline || null,
            homepage_description: subPlanForm.homepage_description || null,
            homepage_features: subPlanForm.homepage_features.filter(f => f.title.trim()),
            homepage_cta_text: subPlanForm.homepage_cta_text || null,
            homepage_cta_link: subPlanForm.homepage_cta_link || null,
            homepage_image_url: subPlanForm.homepage_image_url || null,
            homepage_badge_text: subPlanForm.homepage_badge_text || null,
            homepage_badge_description: subPlanForm.homepage_badge_description || null,
            homepage_visible: subPlanForm.homepage_visible
        };
        try {
            if (editingSubPlan) {
                await axios.put(`${API}/subscription-plans/${editingSubPlan.id}`, data, { headers });
            } else {
                await axios.post(`${API}/subscription-plans`, data, { headers });
            }
            setIsSubPlanModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Failed to save subscription plan');
        }
    };

    const deleteSubPlan = async (planId) => {
        if (!window.confirm('Delete this subscription plan?')) return;
        try {
            await axios.delete(`${API}/subscription-plans/${planId}`, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {}
    };

    const addProductToSubPlan = (product) => {
        const existing = subPlanForm.items.find(i => i.product_id === product.id);
        if (existing) {
            setSubPlanForm({
                ...subPlanForm,
                items: subPlanForm.items.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i)
            });
        } else {
            setSubPlanForm({
                ...subPlanForm,
                items: [...subPlanForm.items, { product_id: product.id, product_name: product.name, quantity: 1 }]
            });
        }
    };

    const removeProductFromSubPlan = (productId) => {
        setSubPlanForm({ ...subPlanForm, items: subPlanForm.items.filter(i => i.product_id !== productId) });
    };

    const updateItemQuantity = (productId, quantity) => {
        if (quantity <= 0) { removeProductFromSubPlan(productId); return; }
        setSubPlanForm({
            ...subPlanForm,
            items: subPlanForm.items.map(i => i.product_id === productId ? { ...i, quantity } : i)
        });
    };

    // === PROMO CODE CRUD ===
    const openPromoModal = (promo = null) => {
        if (promo) {
            setEditingPromo(promo);
            setPromoForm({
                code: promo.code, description: promo.description || '',
                discount_type: promo.discount_type, discount_value: promo.discount_value.toString(),
                min_order_amount: promo.min_order_amount?.toString() || '',
                max_uses: promo.max_uses?.toString() || '', is_active: promo.is_active,
                expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '', applies_to: promo.applies_to
            });
        } else {
            setEditingPromo(null);
            setPromoForm({
                code: '', description: '', discount_type: 'percentage', discount_value: '',
                min_order_amount: '', max_uses: '', is_active: true, expires_at: '', applies_to: 'all'
            });
        }
        setIsPromoModalOpen(true);
    };

    const handlePromoSubmit = async (e) => {
        e.preventDefault();
        const headers = getAuthHeaders();
        const data = {
            code: promoForm.code.toUpperCase(),
            description: promoForm.description || null,
            discount_type: promoForm.discount_type,
            discount_value: parseFloat(promoForm.discount_value),
            min_order_amount: promoForm.min_order_amount ? parseFloat(promoForm.min_order_amount) : null,
            max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
            is_active: promoForm.is_active,
            expires_at: promoForm.expires_at ? new Date(promoForm.expires_at).toISOString() : null,
            applies_to: promoForm.applies_to
        };
        try {
            if (editingPromo) {
                await axios.put(`${API}/promo-codes/${editingPromo.id}`, data, { headers });
            } else {
                await axios.post(`${API}/promo-codes`, data, { headers });
            }
            setIsPromoModalOpen(false);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to save promo code');
        }
    };

    const deletePromo = async (codeId) => {
        if (!window.confirm('Delete this promo code?')) return;
        try {
            await axios.delete(`${API}/promo-codes/${codeId}`, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {}
    };

    // === ORDER MANAGEMENT ===
    const openOrderModal = async (order) => {
        setSelectedOrder(order);
        setOrderActivity([]);
        setIsOrderModalOpen(true);
        try {
            const res = await axios.get(`${API}/orders/${order.id}/activity`, { headers: getAuthHeaders() });
            setOrderActivity(res.data);
        } catch (e) {
            console.error('Failed to fetch order activity:', e);
        }
    };

    const updateOrderStatus = async (orderId, field, value) => {
        try {
            const res = await axios.put(`${API}/orders/${orderId}`, { [field]: value }, { headers: getAuthHeaders() });
            fetchData();
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder(res.data);
                // Refresh activity log
                const actRes = await axios.get(`${API}/orders/${orderId}/activity`, { headers: getAuthHeaders() });
                setOrderActivity(actRes.data);
            }
        } catch (error) {
            alert('Failed to update order');
        }
    };

    // === FULFILLMENT ACTIONS ===
    const updateFulfillmentStatus = async (id, status) => {
        try {
            await axios.put(`${API}/subscription-fulfillments/${id}?status=${status}`, {}, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {
            alert('Failed to update fulfillment');
        }
    };

    const createOrderFromFulfillment = async (id) => {
        try {
            const res = await axios.post(`${API}/subscription-fulfillments/${id}/create-order`, {}, { headers: getAuthHeaders() });
            alert(`Order ${res.data.order_number} created!`);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to create order');
        }
    };

    const generateFulfillments = async () => {
        try {
            const res = await axios.post(`${API}/subscription-fulfillments/generate`, {}, { headers: getAuthHeaders() });
            alert(res.data.message);
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to generate fulfillments');
        }
    };

    // === INVENTORY ===
    const adjustInventory = async (productId, adjustment, reason = '') => {
        try {
            await axios.put(`${API}/inventory/${productId}/adjust?adjustment=${adjustment}&reason=${encodeURIComponent(reason)}`, {}, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {
            alert('Failed to adjust inventory');
        }
    };

    // === CUSTOMERS ===
    const openCustomerDetail = async (email) => {
        try {
            const res = await axios.get(`${API}/customers/${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
            setSelectedCustomer(res.data);
            setIsCustomerModalOpen(true);
        } catch (error) {
            alert('Failed to load customer details');
        }
    };

    const handleCustomerSearch = useCallback(() => {
        if (activeTab === 'customers') fetchData();
    }, [activeTab, fetchData]);

    // === TEAM ===
    const handleAddTeamMember = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/admin/team`, newMemberForm, { headers: getAuthHeaders() });
            setNewMemberForm({ username: '', password: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to add team member');
        }
    };

    const handleRemoveTeamMember = async (memberId, memberUsername) => {
        if (!window.confirm(`Remove ${memberUsername} from the team?`)) return;
        try {
            await axios.delete(`${API}/admin/team/${memberId}`, { headers: getAuthHeaders() });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to remove team member');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-500/20 text-yellow-400',
            paid: 'bg-green-500/20 text-green-400',
            processing: 'bg-blue-500/20 text-blue-400',
            shipped: 'bg-purple-500/20 text-purple-400',
            delivered: 'bg-green-600/20 text-green-500',
            cancelled: 'bg-red-500/20 text-red-400',
            unfulfilled: 'bg-yellow-500/20 text-yellow-400',
            fulfilled: 'bg-green-500/20 text-green-400',
            skipped: 'bg-gray-500/20 text-gray-400'
        };
        return colors[status] || 'bg-gray-500/20 text-gray-400';
    };

    return (
        <div className="min-h-screen bg-maple-admin-bg" data-testid="admin-dashboard">
            {/* Sidebar */}
            <aside className="fixed left-0 top-0 h-full w-64 bg-maple-admin-sidebar border-r border-white/10 p-6 flex flex-col overflow-y-auto">
                <Link to="/" className="mb-8 flex-shrink-0">
                    <img src={LOGO_URL} alt="Wayside Maple" className="h-10 brightness-0 invert" />
                </Link>
                <nav className="flex-1 space-y-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                activeTab === tab.id ? 'bg-maple-red text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                            }`}
                            data-testid={`tab-${tab.id}`}
                        >
                            <tab.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-manrope text-xs uppercase tracking-wider">{tab.label}</span>
                        </button>
                    ))}
                </nav>
                <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-white/60 hover:text-maple-red" data-testid="logout-btn">
                    <LogOut className="w-5 h-5" />
                    <span className="font-manrope text-sm uppercase tracking-wider">Logout</span>
                </button>
            </aside>

            {/* Main Content */}
            <main className="ml-64 p-8">
                <div className="max-w-6xl">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="font-anton text-3xl text-white uppercase">
                            {tabs.find(t => t.id === activeTab)?.label}
                        </h1>
                        {activeTab === 'products' && (
                            <button onClick={() => openProductModal()} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
                                <Plus className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} /><span>Add Product</span>
                            </button>
                        )}
                        {activeTab === 'subscriptions' && (
                            <button onClick={() => openSubPlanModal()} className="btn-primary flex items-center gap-2" data-testid="add-subscription-btn">
                                <Plus className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} /><span>Add Plan</span>
                            </button>
                        )}
                        {activeTab === 'promos' && (
                            <button onClick={() => openPromoModal()} className="btn-primary flex items-center gap-2" data-testid="add-promo-btn">
                                <Plus className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} /><span>Add Promo</span>
                            </button>
                        )}
                        {activeTab === 'wholesaleCustomers' && (
                            <button onClick={resetWholesaleCustomerForm} className="btn-primary flex items-center gap-2">
                                <Plus className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} /><span>New Wholesale Customer</span>
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="w-8 h-8 border-2 border-maple-red border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                    ) : (
                        <>
                            {/* ORDERS TAB */}
                            {activeTab === 'orders' && (
                                <>
                                    {/* Order Stats Cards */}
                                    {orderStats && (
                                        <div className="grid grid-cols-4 gap-4 mb-6" data-testid="order-stats">
                                            <div className="bg-maple-admin-card border border-white/10 p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-white/5 flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-white/40" /></div>
                                                    <div>
                                                        <p className="text-2xl font-mono text-white">{orderStats.total}</p>
                                                        <p className="text-white/40 text-xs uppercase">Paid Orders</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-maple-admin-card border border-yellow-500/20 p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-yellow-500/10 flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-400" /></div>
                                                    <div>
                                                        <p className="text-2xl font-mono text-yellow-400">{orderStats.needs_fulfillment}</p>
                                                        <p className="text-white/40 text-xs uppercase">Needs Fulfillment</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-maple-admin-card border border-green-500/20 p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-400" /></div>
                                                    <div>
                                                        <p className="text-2xl font-mono text-green-400">${(orderStats.paid_revenue ?? orderStats.by_payment?.paid?.total ?? 0).toFixed(0)}</p>
                                                        <p className="text-white/40 text-xs uppercase">Revenue (Paid)</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="bg-maple-admin-card border border-purple-500/20 p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-purple-500/10 flex items-center justify-center"><Truck className="w-5 h-5 text-purple-400" /></div>
                                                    <div>
                                                        <p className="text-2xl font-mono text-purple-400">{orderStats.by_status?.shipped?.count || 0}</p>
                                                        <p className="text-white/40 text-xs uppercase">Shipped</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Filter Row */}
                                    <div className="flex gap-6 mb-6 flex-wrap" data-testid="order-filters">
                                        <div>
                                            <p className="text-white/30 text-[10px] uppercase mb-1.5 tracking-wider">Status</p>
                                            <div className="flex gap-1">
                                                {['placed', 'all', 'pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled'].map(f => (
                                                    <button key={f} onClick={() => setOrderStatusFilter(f)}
                                                        data-testid={`order-status-filter-${f}`}
                                                        className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${orderStatusFilter === f ? 'bg-maple-red text-white' : 'bg-white/5 text-white/50 hover:text-white/80'}`}>
                                                        {f === 'placed' ? 'placed orders' : f}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-white/30 text-[10px] uppercase mb-1.5 tracking-wider">Fulfillment</p>
                                            <div className="flex gap-1">
                                                {['all', 'unfulfilled', 'fulfilled'].map(f => (
                                                    <button key={f} onClick={() => setOrderFulfillmentFilter(f)}
                                                        data-testid={`order-fulfillment-filter-${f}`}
                                                        className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${orderFulfillmentFilter === f ? 'bg-maple-red text-white' : 'bg-white/5 text-white/50 hover:text-white/80'}`}>
                                                        {f}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-white/30 text-[10px] uppercase mb-1.5 tracking-wider">Customer Type</p>
                                            <div className="flex gap-1">
                                                {['all', 'retail', 'wholesale'].map(f => (
                                                    <button key={f} onClick={() => setOrderCustomerTypeFilter(f)}
                                                        className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${orderCustomerTypeFilter === f ? 'bg-maple-red text-white' : 'bg-white/5 text-white/50 hover:text-white/80'}`}>
                                                        {f}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order List */}
                                    <div className="grid gap-3" data-testid="order-list">
                                        {orders.length === 0 ? (
                                            <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                                <ShoppingCart className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                                <p className="text-white/40">No orders found</p>
                                            </div>
                                        ) : orders.map(order => (
                                            <div key={order.id} className="bg-maple-admin-card border border-white/10 p-4 cursor-pointer hover:border-white/20 transition-colors"
                                                onClick={() => openOrderModal(order)} data-testid={`order-row-${order.id}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="font-mono text-white text-sm">{order.order_number || `#${order.id.slice(0,8)}`}</span>
                                                            <span className={`px-2 py-0.5 text-[10px] uppercase font-bold ${getStatusColor(order.status)}`}>
                                                                {order.status}
                                                            </span>
                                                            <span className={`px-2 py-0.5 text-[10px] uppercase ${getStatusColor(order.fulfillment_status)}`}>
                                                                {order.fulfillment_status}
                                                            </span>
                                                            {order.order_type === 'subscription' && (
                                                                <span className="px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 uppercase">Subscription</span>
                                                            )}
                                                            {order.customer_type === 'wholesale' && (
                                                                <span className="px-2 py-0.5 text-[10px] bg-maple-red/20 text-maple-red uppercase">Wholesale</span>
                                                            )}
                                                            {order.tracking_number && (
                                                                <span className="px-2 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 uppercase flex items-center gap-1">
                                                                    <Truck className="w-3 h-3" /> Tracked
                                                                </span>
                                                            )}
                                                            {order.payment_status === 'paid' && order.fulfillment_method !== 'local_pickup' && !order.shipping_address && (
                                                                <span className="px-2 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-300 uppercase">Missing shipping address</span>
                                                            )}
                                                        </div>
                                                        <p className="text-white/50 text-sm mt-1">{order.customer_name || order.customer_email || 'No customer info'}</p>
                                                        <p className="text-white/30 text-xs mt-0.5">
                                                            {order.items?.length} item(s) &middot; {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            {order.paid_at && ` &middot; Paid ${new Date(order.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                                        </p>
                                                    </div>
                                                    <div className="text-right ml-4">
                                                        <span className="font-mono text-maple-red text-lg">${order.total?.toFixed(2)}</span>
                                                        <p className={`text-[10px] mt-1 uppercase ${getStatusColor(order.payment_status)}`}>{order.payment_status}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* FULFILLMENTS TAB */}
                            {activeTab === 'fulfillments' && (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex gap-2">
                                            {['pending', 'processing', 'fulfilled', 'all'].map(f => (
                                                <button key={f} onClick={() => setFulfillmentFilter(f)}
                                                    className={`px-4 py-2 text-xs uppercase tracking-wider ${fulfillmentFilter === f ? 'bg-maple-red text-white' : 'bg-white/5 text-white/60'}`}>
                                                    {f}
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            onClick={generateFulfillments}
                                            className="btn-primary flex items-center gap-2"
                                            data-testid="generate-fulfillments-btn"
                                        >
                                            <Repeat className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} />
                                            <span>Generate Due Fulfillments</span>
                                        </button>
                                    </div>
                                    <div className="grid gap-4">
                                        {fulfillments.length === 0 ? (
                                            <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                                <Truck className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                                <p className="text-white/40">No subscription fulfillments in queue</p>
                                                <p className="text-white/20 text-sm mt-2">Click "Generate Due Fulfillments" to check for active subscriptions that need fulfilling</p>
                                            </div>
                                        ) : fulfillments.map(f => (
                                            <div key={f.id} className="bg-maple-admin-card border border-white/10 p-4">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h3 className="font-anton text-white">{f.plan_name}</h3>
                                                        <p className="text-white/50 text-sm">{f.customer_email}</p>
                                                        <p className="text-white/40 text-xs mt-1">
                                                            Scheduled: {new Date(f.scheduled_date).toLocaleDateString()}
                                                        </p>
                                                        {f.items && f.items.length > 0 && (
                                                            <p className="text-white/30 text-xs mt-1">
                                                                Items: {f.items.map(i => `${i.quantity}x ${i.product_name}`).join(', ')}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 text-xs uppercase ${getStatusColor(f.status)}`}>{f.status}</span>
                                                        {f.status === 'pending' && (
                                                            <>
                                                                <button onClick={() => createOrderFromFulfillment(f.id)} 
                                                                    className="px-3 py-1 bg-maple-red text-white text-xs" data-testid={`create-order-${f.id}`}>Create Order</button>
                                                                <button onClick={() => updateFulfillmentStatus(f.id, 'skipped')}
                                                                    className="px-3 py-1 bg-white/10 text-white/60 text-xs" data-testid={`skip-${f.id}`}>Skip</button>
                                                            </>
                                                        )}
                                                        {f.order_id && (
                                                            <span className="text-white/40 text-xs">Order created</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* PRODUCTS TAB */}
                            {activeTab === 'products' && (
                                <div className="grid gap-4">
                                    {products.length === 0 ? (
                                        <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                            <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                            <p className="text-white/40">No products yet</p>
                                        </div>
                                    ) : products.map(product => (
                                        <div key={product.id} className="bg-maple-admin-card border border-white/10 p-4 flex items-center gap-4">
                                            <div className="w-16 h-16 bg-maple-secondary flex-shrink-0 overflow-hidden">
                                                {normalizeRenderableImageUrl(product.main_image || product.images?.[0]) ? <img src={normalizeRenderableImageUrl(product.main_image || product.images?.[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">No img</div>}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-anton text-lg text-white">{product.name}</h3>
                                                    {product.featured && <Star className="w-4 h-4 text-maple-red fill-current" />}
                                                </div>
                                                <p className="text-white/40 text-sm">{product.category} • ${product.price.toFixed(2)}</p>
                                                {product.wholesale_enabled && (
                                                    <p className="text-maple-red text-xs mt-1 uppercase tracking-wider">
                                                        Wholesale: {product.wholesale_batch_size || '—'} {product.wholesale_unit_label || 'units'} • ${product.wholesale_price != null ? Number(product.wholesale_price).toFixed(2) : '0.00'} per batch
                                                    </p>
                                                )}
                                                {product.track_inventory && (
                                                    <p className={`text-xs mt-1 ${product.stock_quantity <= product.low_stock_threshold ? 'text-yellow-400' : 'text-white/40'}`}>
                                                        Stock: {product.stock_quantity}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => toggleFeatured(product)} className={`p-2 ${product.featured ? 'text-maple-red' : 'text-white/40 hover:text-white'}`}><Star className="w-5 h-5" /></button>
                                                <button onClick={() => openProductModal(product)} className="p-2 text-white/40 hover:text-white"><Pencil className="w-5 h-5" /></button>
                                                <button onClick={() => deleteProduct(product.id)} className="p-2 text-white/40 hover:text-maple-red"><Trash2 className="w-5 h-5" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* SUBSCRIPTION PLANS TAB */}
                            {activeTab === 'subscriptions' && (
                                <div className="grid gap-4">
                                    {subscriptionPlans.length === 0 ? (
                                        <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                            <Repeat className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                            <p className="text-white/40">No subscription plans yet</p>
                                        </div>
                                    ) : subscriptionPlans.map(plan => (
                                        <div key={plan.id} className={`bg-maple-admin-card border p-4 ${plan.is_featured ? 'border-maple-red' : plan.is_active ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 flex items-start gap-4">
                                                    {normalizeRenderableImageUrl(plan.homepage_image_url || plan.image) && (
                                                        <div className="w-16 h-16 bg-maple-secondary flex-shrink-0 overflow-hidden border border-white/10">
                                                            <img src={normalizeRenderableImageUrl(plan.homepage_image_url || plan.image)} alt="" className="w-full h-full object-cover" />
                                                        </div>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="font-anton text-lg text-white">{plan.name}</h3>
                                                            {plan.is_featured && <span className="bg-maple-red px-2 py-0.5 text-xs text-white">Featured on Homepage</span>}
                                                            {plan.is_popular && <span className="bg-white/20 px-2 py-0.5 text-xs text-white/80">Popular</span>}
                                                            {!plan.is_active && <span className="bg-white/20 px-2 py-0.5 text-xs text-white/60">Inactive</span>}
                                                        </div>
                                                    <p className="text-white/40 text-sm mt-1">{plan.description}</p>
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <span className="font-mono text-maple-red">${plan.price.toFixed(2)}</span>
                                                        <span className="text-white/40 text-sm">every {plan.interval_months} months</span>
                                                    </div>
                                                    {plan.items?.length > 0 && (
                                                        <p className="mt-2 text-white/50 text-sm">
                                                            Includes: {plan.items.map(i => `${i.quantity}x ${i.product_name}`).join(', ')}
                                                        </p>
                                                    )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={async () => {
                                                        try {
                                                            await axios.put(`${API}/subscription-plans/${plan.id}`, { is_featured: !plan.is_featured }, { headers: getAuthHeaders() });
                                                            fetchData();
                                                        } catch (e) { alert('Failed to update'); }
                                                    }} className={`p-2 ${plan.is_featured ? 'text-maple-red' : 'text-white/30 hover:text-maple-red'}`} title={plan.is_featured ? 'Remove from homepage' : 'Feature on homepage'}
                                                        data-testid={`feature-plan-${plan.id}`}>
                                                        <Star className="w-5 h-5" fill={plan.is_featured ? 'currentColor' : 'none'} />
                                                    </button>
                                                    <button onClick={() => openSubPlanModal(plan)} className="p-2 text-white/40 hover:text-white"><Pencil className="w-5 h-5" /></button>
                                                    <button onClick={() => deleteSubPlan(plan.id)} className="p-2 text-white/40 hover:text-maple-red"><Trash2 className="w-5 h-5" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* PROMO CODES TAB */}
                            {activeTab === 'promos' && (
                                <div className="grid gap-4">
                                    {promoCodes.length === 0 ? (
                                        <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                            <Tag className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                            <p className="text-white/40">No promo codes yet</p>
                                        </div>
                                    ) : promoCodes.map(promo => (
                                        <div key={promo.id} className={`bg-maple-admin-card border p-4 ${promo.is_active ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-lg text-maple-red">{promo.code}</span>
                                                        {!promo.is_active && <span className="bg-white/20 px-2 py-0.5 text-xs text-white/60">Inactive</span>}
                                                    </div>
                                                    <p className="text-white/60 text-sm mt-1">
                                                        {promo.discount_type === 'percentage' ? `${promo.discount_value}% off` : `$${promo.discount_value} off`}
                                                        {promo.min_order_amount && ` (min $${promo.min_order_amount})`}
                                                    </p>
                                                    <p className="text-white/40 text-xs mt-1">
                                                        Used: {promo.times_used || 0}{promo.max_uses ? ` / ${promo.max_uses}` : ''} times
                                                        {promo.expires_at && ` • Expires: ${new Date(promo.expires_at).toLocaleDateString()}`}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => openPromoModal(promo)} className="p-2 text-white/40 hover:text-white"><Pencil className="w-5 h-5" /></button>
                                                    <button onClick={() => deletePromo(promo.id)} className="p-2 text-white/40 hover:text-maple-red"><Trash2 className="w-5 h-5" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* INVENTORY TAB */}
                            {activeTab === 'inventory' && (
                                <>
                                    {lowStockProducts.length > 0 && (
                                        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30">
                                            <div className="flex items-center gap-2 text-yellow-400 mb-2">
                                                <AlertTriangle className="w-5 h-5" />
                                                <span className="font-anton">LOW STOCK ALERT</span>
                                            </div>
                                            <div className="space-y-2">
                                                {lowStockProducts.map(p => (
                                                    <div key={p.id} className="flex justify-between items-center text-sm">
                                                        <span className="text-white/80">{p.name}</span>
                                                        <span className="text-yellow-400 font-mono">{p.stock_quantity} left</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid gap-4">
                                        {products.filter(p => p.track_inventory).length === 0 ? (
                                            <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                                <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                                <p className="text-white/40">No products with inventory tracking</p>
                                                <p className="text-white/20 text-sm mt-2">Enable "Track Inventory" on products to manage stock</p>
                                            </div>
                                        ) : products.filter(p => p.track_inventory).map(product => (
                                            <div key={product.id} className="bg-maple-admin-card border border-white/10 p-4 flex items-center gap-4">
                                                <div className="flex-1">
                                                    <h3 className="font-anton text-white">{product.name}</h3>
                                                    <p className="text-white/40 text-sm">{product.sku || 'No SKU'}</p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-center">
                                                        <p className={`font-mono text-2xl ${product.stock_quantity <= product.low_stock_threshold ? 'text-yellow-400' : 'text-white'}`}>
                                                            {product.stock_quantity}
                                                        </p>
                                                        <p className="text-white/40 text-xs">in stock</p>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button onClick={() => adjustInventory(product.id, -1, 'Manual adjustment')} className="w-8 h-8 bg-white/10 text-white flex items-center justify-center hover:bg-red-500/30">-</button>
                                                        <button onClick={() => adjustInventory(product.id, 1, 'Manual adjustment')} className="w-8 h-8 bg-white/10 text-white flex items-center justify-center hover:bg-green-500/30">+</button>
                                                        <button onClick={() => {
                                                            const qty = prompt('Set stock quantity:', product.stock_quantity);
                                                            if (qty !== null) adjustInventory(product.id, parseInt(qty) - product.stock_quantity, 'Manual set');
                                                        }} className="px-3 h-8 bg-white/10 text-white/60 text-xs hover:bg-white/20">Set</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* SHIPPING TAB */}
                            {activeTab === 'shipping' && (
                                <div className="bg-maple-admin-card border border-white/10 p-6 max-w-3xl space-y-6" data-testid="shipping-settings-panel">
                                    <div>
                                        <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Checkout shipping rules</p>
                                        <h2 className="font-anton text-2xl text-white">Shipping Settings</h2>
                                        <p className="text-white/50 text-sm mt-2">Use dummy values now, then replace them when the client confirms their real shipping policy.</p>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <label className="flex items-center gap-3 text-white/80 text-sm"><input type="checkbox" checked={shippingSettings.enabled} onChange={e => setShippingSettings({...shippingSettings, enabled: e.target.checked})} /> Enable shipping charges</label>
                                        <label className="flex items-center gap-3 text-white/80 text-sm"><input type="checkbox" checked={shippingSettings.subscription_free_shipping} onChange={e => setShippingSettings({...shippingSettings, subscription_free_shipping: e.target.checked})} /> Free shipping for subscriptions</label>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Mode</label>
                                            <select value={shippingSettings.mode} onChange={e => setShippingSettings({...shippingSettings, mode: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white">
                                                <option value="flat_rate">Flat rate</option>
                                                <option value="product_shipping">Per-product shipping</option>
                                                <option value="threshold">Free over threshold</option>
                                                <option value="free">Always free</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Allowed countries</label>
                                            <input type="text" value={shippingSettings.allowed_countries} onChange={e => setShippingSettings({...shippingSettings, allowed_countries: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" placeholder="US, CA" />
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Shipping label</label>
                                            <input type="text" value={shippingSettings.label} onChange={e => setShippingSettings({...shippingSettings, label: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Free shipping label</label>
                                            <input type="text" value={shippingSettings.free_label} onChange={e => setShippingSettings({...shippingSettings, free_label: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Flat rate</label>
                                            <input type="number" step="0.01" min="0" value={shippingSettings.flat_rate} onChange={e => setShippingSettings({...shippingSettings, flat_rate: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Free shipping threshold</label>
                                            <input type="number" step="0.01" min="0" value={shippingSettings.free_over} onChange={e => setShippingSettings({...shippingSettings, free_over: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                        </div>
                                    </div>

                                    <div className="bg-maple-admin-bg border border-white/10 p-4 text-sm text-white/70">
                                        <p><span className="text-white font-medium">Current test behavior:</span> {shippingSettings.mode === 'free' ? 'all one-time orders ship free' : shippingSettings.mode === 'product_shipping' ? 'checkout uses each product’s Shipping / Handling value multiplied by quantity; falls back to flat rate when no product shipping is set' : shippingSettings.mode === 'threshold' ? `$${Number(shippingSettings.flat_rate || 0).toFixed(2)} shipping until cart reaches $${Number(shippingSettings.free_over || 0).toFixed(2)}` : `flat $${Number(shippingSettings.flat_rate || 0).toFixed(2)} shipping on one-time orders`}</p>
                                        <p className="mt-2">Subscriptions: {shippingSettings.subscription_free_shipping ? 'free shipping' : 'same shipping rules as checkout'}</p>
                                    </div>

                                    <div>
                                        <button onClick={saveShippingSettings} className="btn-primary" data-testid="save-shipping-settings">Save Shipping Settings</button>
                                    </div>
                                </div>
                            )}

                            {/* CONTACTS TAB */}
                            {activeTab === 'contacts' && (
                                <div className="grid gap-4">
                                    {contacts.length === 0 ? (
                                        <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                            <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                            <p className="text-white/40">No messages yet</p>
                                        </div>
                                    ) : contacts.map(contact => (
                                        <div key={contact.id} className={`bg-maple-admin-card border p-4 ${contact.is_read ? 'border-white/10' : 'border-maple-red/50'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-anton text-lg text-white">{contact.subject}</h3>
                                                <span className="text-white/40 text-xs font-mono">{new Date(contact.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <p className="text-white/60 text-sm mb-2">From: {contact.name} ({contact.email})</p>
                                            <p className="text-white/70">{contact.message}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* CUSTOMERS TAB */}
                            {activeTab === 'customers' && (
                                <>
                                    {/* Search Bar */}
                                    <div className="mb-6 flex gap-3" data-testid="customer-search">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                                            <input
                                                type="text"
                                                value={customerSearch}
                                                onChange={e => setCustomerSearch(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleCustomerSearch()}
                                                placeholder="Search by name or email..."
                                                className="w-full bg-maple-admin-bg border border-white/20 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/30"
                                                data-testid="customer-search-input"
                                            />
                                        </div>
                                        <button onClick={handleCustomerSearch} className="px-5 py-2.5 bg-maple-red text-white text-sm uppercase tracking-wider hover:bg-maple-red/80 transition-colors" data-testid="customer-search-btn">Search</button>
                                    </div>

                                    {/* Customer List */}
                                    <div className="grid gap-3" data-testid="customer-list">
                                        {customers.length === 0 ? (
                                            <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                                <p className="text-white/40">No customers found</p>
                                            </div>
                                        ) : customers.map(customer => (
                                            <div key={customer.email} onClick={() => openCustomerDetail(customer.email)}
                                                className="bg-maple-admin-card border border-white/10 p-4 cursor-pointer hover:border-white/20 transition-colors"
                                                data-testid={`customer-row-${customer.email}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 bg-white/5 flex items-center justify-center text-white/40 text-sm font-mono uppercase">
                                                                {(customer.name || customer.email || '?').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <p className="text-white font-medium">{customer.name || 'No name'}</p>
                                                                <p className="text-white/50 text-sm">{customer.email}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-8 text-right">
                                                        <div>
                                                            <p className="text-white font-mono">{customer.total_orders}</p>
                                                            <p className="text-white/30 text-[10px] uppercase">Orders</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-maple-red font-mono">${customer.total_spent?.toFixed(2)}</p>
                                                            <p className="text-white/30 text-[10px] uppercase">Total Spent</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-white/60 font-mono text-xs">{customer.last_order ? new Date(customer.last_order).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                                                            <p className="text-white/30 text-[10px] uppercase">Last Order</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* WHOLESALE CUSTOMERS TAB */}
                            {activeTab === 'wholesaleCustomers' && (
                                <div className="grid lg:grid-cols-[420px_1fr] gap-6">
                                    <div className="bg-maple-admin-card border border-white/10 p-5">
                                        <p className="text-white/40 text-xs uppercase mb-4 tracking-wider">
                                            {wholesaleCustomerForm.id ? 'Edit Wholesale Customer' : 'Add Wholesale Customer'}
                                        </p>
                                        <form onSubmit={saveWholesaleCustomer} className="space-y-4">
                                            <div>
                                                <label className="block text-white/40 text-xs uppercase mb-1">Full Name</label>
                                                <input type="text" value={wholesaleCustomerForm.full_name} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, full_name: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                            </div>
                                            <div>
                                                <label className="block text-white/40 text-xs uppercase mb-1">Email</label>
                                                <input type="email" value={wholesaleCustomerForm.email} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, email: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                            </div>
                                            <div>
                                                <label className="block text-white/40 text-xs uppercase mb-1">Business Name</label>
                                                <input type="text" value={wholesaleCustomerForm.business_name} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, business_name: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                            </div>
                                            {!wholesaleCustomerForm.id && (
                                                <div>
                                                    <label className="block text-white/40 text-xs uppercase mb-1">Default Password</label>
                                                    <input type="text" value={wholesaleCustomerForm.default_password} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, default_password: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white" />
                                                </div>
                                            )}
                                            <div>
                                                <label className="block text-white/40 text-xs uppercase mb-1">Notes</label>
                                                <textarea value={wholesaleCustomerForm.notes} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, notes: e.target.value})} rows={4} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-sm text-white resize-none" />
                                            </div>
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={wholesaleCustomerForm.is_active} onChange={e => setWholesaleCustomerForm({...wholesaleCustomerForm, is_active: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">Active</span></label>
                                            <div className="flex gap-3">
                                                <button type="submit" className="btn-primary">{wholesaleCustomerForm.id ? 'Update Customer' : 'Create Customer'}</button>
                                                <button type="button" onClick={resetWholesaleCustomerForm} className="px-5 py-3 border border-white/20 text-white/70 text-sm uppercase tracking-wider">Clear</button>
                                            </div>
                                        </form>
                                    </div>
                                    <div className="grid gap-3">
                                        {wholesaleCustomers.length === 0 ? (
                                            <div className="text-center py-20 border border-white/10 bg-maple-admin-card">
                                                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                                <p className="text-white/40">No wholesale customers yet</p>
                                            </div>
                                        ) : wholesaleCustomers.map(customer => (
                                            <div key={customer.id} className="bg-maple-admin-card border border-white/10 p-4 flex items-start justify-between gap-4">
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <h3 className="font-anton text-white text-lg">{customer.full_name}</h3>
                                                        {!customer.is_active && <span className="px-2 py-0.5 text-[10px] uppercase bg-white/10 text-white/50">Inactive</span>}
                                                    </div>
                                                    <p className="text-white/50 text-sm mt-1">{customer.email}</p>
                                                    {customer.business_name && <p className="text-white/30 text-xs mt-1 uppercase tracking-wider">{customer.business_name}</p>}
                                                    {customer.notes && <p className="text-white/40 text-sm mt-3 max-w-xl">{customer.notes}</p>}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => editWholesaleCustomer(customer)} className="p-2 text-white/40 hover:text-white"><Pencil className="w-5 h-5" /></button>
                                                    <button onClick={() => resetWholesalePassword(customer.id)} className="px-3 py-2 border border-white/20 text-white/60 text-xs uppercase tracking-wider hover:text-white">Reset Password</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* TEAM TAB */}
                            {activeTab === 'team' && (
                                <div className="space-y-6">
                                    {/* Add Member Form */}
                                    <div className="bg-maple-admin-card border border-white/10 p-5" data-testid="add-team-form">
                                        <p className="text-white/40 text-xs uppercase mb-4 tracking-wider">Add Team Member</p>
                                        <form onSubmit={handleAddTeamMember} className="flex gap-3 items-end">
                                            <div className="flex-1">
                                                <label className="block text-white/40 text-xs uppercase mb-1">Email</label>
                                                <input type="email" value={newMemberForm.username} onChange={e => setNewMemberForm({...newMemberForm, username: e.target.value})}
                                                    required placeholder="team@waysidemaple.com"
                                                    className="w-full bg-maple-admin-bg border border-white/20 p-2.5 text-sm text-white placeholder:text-white/30"
                                                    data-testid="team-email-input" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-white/40 text-xs uppercase mb-1">Password</label>
                                                <input type="password" value={newMemberForm.password} onChange={e => setNewMemberForm({...newMemberForm, password: e.target.value})}
                                                    required placeholder="Set a password"
                                                    className="w-full bg-maple-admin-bg border border-white/20 p-2.5 text-sm text-white placeholder:text-white/30"
                                                    data-testid="team-password-input" />
                                            </div>
                                            <button type="submit" className="px-6 py-2.5 bg-maple-red text-white text-sm uppercase tracking-wider hover:bg-maple-red/80 transition-colors whitespace-nowrap" data-testid="team-add-btn">
                                                Add Member
                                            </button>
                                        </form>
                                    </div>

                                    {/* Team Members List */}
                                    <div data-testid="team-list">
                                        <p className="text-white/40 text-xs uppercase mb-3 tracking-wider">Team Members ({teamMembers.length})</p>
                                        <div className="grid gap-3">
                                            {teamMembers.map(member => (
                                                <div key={member.id} className="bg-maple-admin-card border border-white/10 p-4 flex justify-between items-center" data-testid={`team-member-${member.id}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 bg-white/5 flex items-center justify-center text-white/40 text-sm font-mono uppercase">
                                                            {(member.username || '?').charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="text-white font-medium">{member.username}</p>
                                                            <p className="text-white/30 text-xs font-mono">
                                                                Added {member.created_at ? new Date(member.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => handleRemoveTeamMember(member.id, member.username)}
                                                        className="p-2 text-white/20 hover:text-red-400 transition-colors"
                                                        title="Remove member"
                                                        data-testid={`team-remove-${member.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Product Modal */}
            <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
                <DialogContent className="bg-maple-admin-card border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-anton text-2xl">{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
                    <form onSubmit={handleProductSubmit} className="space-y-4">
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Name</label>
                            <input type="text" value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-name-input" /></div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Description</label>
                            <textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} required rows={3} className="w-full bg-maple-admin-bg border border-white/20 p-3 resize-none text-white placeholder:text-white/30" data-testid="product-desc-input" /></div>
                        <div className="grid grid-cols-3 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Price ($)</label>
                                <input type="number" step="0.01" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-price-input" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Compare At ($)</label>
                                <input type="number" step="0.01" value={productForm.compare_at_price} onChange={e => setProductForm({...productForm, compare_at_price: e.target.value})} placeholder="Optional" className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-compare-price-input" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Category</label>
                                <input type="text" value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-category-input" /></div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Product Shipping / Handling ($ per unit)</label>
                            <input type="number" step="0.01" min="0" value={productForm.shipping_cost} onChange={e => setProductForm({...productForm, shipping_cost: e.target.value})} placeholder="Leave blank to use store shipping rule" className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-shipping-cost-input" />
                            <p className="text-white/30 text-xs mt-1">When any cart item has this configured, checkout uses product-specific shipping totals instead of the flat fallback.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Size Value</label>
                                <input type="number" step="0.1" value={productForm.size_value} onChange={e => setProductForm({...productForm, size_value: e.target.value})} placeholder="e.g. 16" className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-size-value-input" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Size Unit</label>
                                <select value={productForm.size_unit} onChange={e => setProductForm({...productForm, size_unit: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white" data-testid="product-size-unit-select">
                                    <option value="">None</option>
                                    <option value="oz">Ounces (oz)</option>
                                    <option value="quart">Quart</option>
                                    <option value="half_gallon">Half Gallon</option>
                                    <option value="gallon">Gallon</option>
                                    <option value="ml">Milliliters (ml)</option>
                                    <option value="liter">Liter</option>
                                </select></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Syrup Grade Options</label>
                                <textarea value={productForm.grade_options_text} onChange={e => setProductForm({...productForm, grade_options_text: e.target.value})} rows={4} placeholder={'Delicate\nAmber Rich\nDark Robust\nVery Dark Strong'} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30 resize-none" data-testid="product-grade-options-input" />
                                <p className="text-white/30 text-xs mt-1">Use only on plastic jug syrup products. One grade per line.</p>
                            </div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Default Grade</label>
                                <input type="text" value={productForm.default_grade} onChange={e => setProductForm({...productForm, default_grade: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-default-grade-input" />
                                <button type="button" onClick={() => setProductForm({...productForm, grade_options_text: 'Delicate\nAmber Rich\nDark Robust\nVery Dark Strong', default_grade: 'Dark Robust'})} className="mt-3 px-3 py-2 bg-white/10 hover:bg-white/20 text-white/70 text-xs uppercase tracking-wider">Apply plastic jug grades</button>
                            </div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Related Products (other sizes)</label>
                            <div className="space-y-2">
                                {products.filter(p => !editingProduct || p.id !== editingProduct.id).map(p => (
                                    <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={productForm.related_product_ids.includes(p.id)}
                                            onChange={e => {
                                                const ids = e.target.checked
                                                    ? [...productForm.related_product_ids, p.id]
                                                    : productForm.related_product_ids.filter(id => id !== p.id);
                                                setProductForm({...productForm, related_product_ids: ids});
                                            }} className="w-4 h-4" />
                                        <span className="text-white/70 text-sm">{p.name}{p.size_value ? ` (${p.size_value} ${p.size_unit || ''})` : ''} — ${p.price}</span>
                                    </label>
                                ))}
                                {products.filter(p => !editingProduct || p.id !== editingProduct.id).length === 0 && (
                                    <p className="text-white/30 text-xs">No other products to link</p>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-white/40 text-xs uppercase mb-2">Product Images</label>
                            <div className="border border-white/20 p-4 bg-maple-admin-bg">
                                {/* Upload button */}
                                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 hover:border-maple-red p-4 cursor-pointer transition-colors mb-4" data-testid="image-upload-area">
                                    <Upload className="w-5 h-5 text-white/40" />
                                    <span className="text-white/40 text-sm">{uploading ? 'Uploading...' : 'Click to upload images'}</span>
                                    <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} data-testid="image-file-input" />
                                </label>
                                {/* Image grid */}
                                {productForm.images.length > 0 && (
                                    <div className="grid grid-cols-4 gap-3">
                                        {productForm.images.map((img, idx) => (
                                            <div key={idx} className={`relative group border-2 ${productForm.main_image === img ? 'border-maple-red' : 'border-white/10'}`}>
                                                <img src={normalizeRenderableImageUrl(img)} alt={`Product ${idx + 1}`} className="w-full h-24 object-cover" />
                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <button type="button" onClick={() => setProductForm({ ...productForm, main_image: img })}
                                                        className={`p-1 ${productForm.main_image === img ? 'text-maple-red' : 'text-white/60 hover:text-maple-red'}`}
                                                        title="Set as main image" data-testid={`set-main-${idx}`}>
                                                        <Star className="w-4 h-4" fill={productForm.main_image === img ? 'currentColor' : 'none'} />
                                                    </button>
                                                    <button type="button" onClick={() => {
                                                        const newImages = productForm.images.filter((_, i) => i !== idx);
                                                        const newMain = productForm.main_image === img ? (newImages[0] || '') : productForm.main_image;
                                                        setProductForm({ ...productForm, images: newImages, main_image: newMain });
                                                    }} className="p-1 text-white/60 hover:text-red-500" title="Remove" data-testid={`remove-image-${idx}`}>
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                {productForm.main_image === img && (
                                                    <div className="absolute top-1 left-1 bg-maple-red px-1.5 py-0.5">
                                                        <span className="text-white text-[10px] font-bold uppercase">Main</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* URL input fallback */}
                                <div className="mt-3 flex gap-2">
                                    <input type="text" placeholder="Or paste image URL and press Enter" className="flex-1 bg-maple-admin-bg border border-white/10 p-2 text-sm text-white"
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const url = e.target.value.trim();
                                                if (url) {
                                                    const newImages = [...productForm.images, url];
                                                    setProductForm({ ...productForm, images: newImages, main_image: productForm.main_image || url });
                                                    e.target.value = '';
                                                }
                                            }
                                        }} data-testid="image-url-input" />
                                </div>
                            </div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">SKU</label>
                            <input type="text" value={productForm.sku} onChange={e => setProductForm({...productForm, sku: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" data-testid="product-sku-input" /></div>
                        <div className="border-t border-white/10 pt-4">
                            <label className="flex items-center gap-2 cursor-pointer mb-3">
                                <input type="checkbox" checked={productForm.track_inventory} onChange={e => setProductForm({...productForm, track_inventory: e.target.checked})} className="w-4 h-4" />
                                <span className="text-white/70 text-sm">Track Inventory</span>
                            </label>
                            {productForm.track_inventory && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Stock Quantity</label>
                                        <input type="number" value={productForm.stock_quantity} onChange={e => setProductForm({...productForm, stock_quantity: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Low Stock Alert</label>
                                        <input type="number" value={productForm.low_stock_threshold} onChange={e => setProductForm({...productForm, low_stock_threshold: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={productForm.featured} onChange={e => setProductForm({...productForm, featured: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">Featured</span></label>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={productForm.in_stock} onChange={e => setProductForm({...productForm, in_stock: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">In Stock</span></label>
                        </div>
                        <div className="border-t border-white/10 pt-4 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-maple-red text-xs uppercase tracking-wider">Wholesale Settings</p>
                                    <p className="text-white/40 text-sm mt-1">Configure batch ordering for the wholesale portal without exposing it on the retail storefront.</p>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={productForm.wholesale_enabled} onChange={e => setProductForm({...productForm, wholesale_enabled: e.target.checked})} className="w-4 h-4" />
                                    <span className="text-white/70 text-sm">Enable Wholesale</span>
                                </label>
                            </div>
                            {productForm.wholesale_enabled && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white/40 text-xs uppercase mb-1">Wholesale Price ($ / batch)</label>
                                        <input type="number" step="0.01" value={productForm.wholesale_price} onChange={e => setProductForm({...productForm, wholesale_price: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" />
                                    </div>
                                    <div>
                                        <label className="block text-white/40 text-xs uppercase mb-1">Batch Size</label>
                                        <input type="number" value={productForm.wholesale_batch_size} onChange={e => setProductForm({...productForm, wholesale_batch_size: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" />
                                    </div>
                                    <div>
                                        <label className="block text-white/40 text-xs uppercase mb-1">Wholesale Unit Label</label>
                                        <input type="text" value={productForm.wholesale_unit_label} onChange={e => setProductForm({...productForm, wholesale_unit_label: e.target.value})} placeholder="case, 12-pack, etc." className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" />
                                    </div>
                                    <div>
                                        <label className="block text-white/40 text-xs uppercase mb-1">Wholesale Category</label>
                                        <input type="text" value={productForm.wholesale_category} onChange={e => setProductForm({...productForm, wholesale_category: e.target.value})} placeholder="Optional category override" className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsProductModalOpen(false)} className="border-white/20">Cancel</Button>
                            <Button type="submit" className="bg-maple-red hover:bg-maple-red/80">{editingProduct ? 'Update' : 'Create'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Subscription Plan Modal */}
            <Dialog open={isSubPlanModalOpen} onOpenChange={setIsSubPlanModalOpen}>
                <DialogContent className="bg-maple-admin-card border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-anton text-2xl">{editingSubPlan ? 'Edit Plan' : 'Add Plan'}</DialogTitle></DialogHeader>
                    <form onSubmit={handleSubPlanSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Plan Name</label>
                                <input type="text" value={subPlanForm.name} onChange={e => setSubPlanForm({...subPlanForm, name: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Interval</label>
                                <select value={subPlanForm.interval_months} onChange={e => setSubPlanForm({...subPlanForm, interval_months: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30">
                                    <option value="1">Monthly</option><option value="3">Quarterly</option><option value="6">Every 6 months</option><option value="12">Yearly</option>
                                </select></div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Description</label>
                            <textarea value={subPlanForm.description} onChange={e => setSubPlanForm({...subPlanForm, description: e.target.value})} required rows={2} className="w-full bg-maple-admin-bg border border-white/20 p-3 resize-none text-white placeholder:text-white/30" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Price ($)</label>
                                <input type="number" step="0.01" value={subPlanForm.price} onChange={e => setSubPlanForm({...subPlanForm, price: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Original Price (optional)</label>
                                <input type="number" step="0.01" value={subPlanForm.original_price} onChange={e => setSubPlanForm({...subPlanForm, original_price: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-2">Included Products</label>
                            {subPlanForm.items.length > 0 && <div className="mb-3 space-y-2">{subPlanForm.items.map(item => (
                                <div key={item.product_id} className="flex items-center gap-3 bg-maple-admin-bg p-2 border border-white/20">
                                    <span className="flex-1 text-white/80 text-sm">{item.product_name}</span>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => updateItemQuantity(item.product_id, item.quantity - 1)} className="w-6 h-6 bg-white/10 text-white">-</button>
                                        <span className="w-8 text-center font-mono">{item.quantity}</span>
                                        <button type="button" onClick={() => updateItemQuantity(item.product_id, item.quantity + 1)} className="w-6 h-6 bg-white/10 text-white">+</button>
                                    </div>
                                    <button type="button" onClick={() => removeProductFromSubPlan(item.product_id)} className="p-1 text-white/40 hover:text-maple-red"><X className="w-4 h-4" /></button>
                                </div>
                            ))}</div>}
                            <div className="max-h-32 overflow-y-auto bg-maple-admin-bg border border-white/20 p-2">
                                {availableProducts.filter(p => !subPlanForm.items.find(i => i.product_id === p.id)).map(product => (
                                    <button key={product.id} type="button" onClick={() => addProductToSubPlan(product)} className="w-full flex items-center justify-between p-2 hover:bg-white/5 text-left">
                                        <span className="text-white/70 text-sm">{product.name}</span>
                                        <span className="text-white/40 text-xs">${product.price.toFixed(2)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Features (one per line)</label>
                            <textarea value={subPlanForm.features} onChange={e => setSubPlanForm({...subPlanForm, features: e.target.value})} rows={3} className="w-full bg-maple-admin-bg border border-white/20 p-3 resize-none text-white placeholder:text-white/30" placeholder="Free shipping&#10;Skip anytime" /></div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={subPlanForm.is_active} onChange={e => setSubPlanForm({...subPlanForm, is_active: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">Active</span></label>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={subPlanForm.is_popular} onChange={e => setSubPlanForm({...subPlanForm, is_popular: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">Popular</span></label>
                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={subPlanForm.is_featured} onChange={e => setSubPlanForm({...subPlanForm, is_featured: e.target.checked})} className="w-4 h-4" data-testid="sub-featured-toggle" /><span className="text-maple-red text-sm font-semibold">Featured on Homepage</span></label>
                        </div>
                        {subPlanForm.is_featured && (
                            <div className="border border-maple-red/30 p-4 space-y-3 mt-2">
                                <p className="text-maple-red text-xs font-mono uppercase tracking-wider">Homepage Display Settings</p>
                                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={subPlanForm.homepage_visible} onChange={e => setSubPlanForm({...subPlanForm, homepage_visible: e.target.checked})} className="w-4 h-4" data-testid="hp-visible" /><span className="text-white/70 text-sm">Visible on Homepage</span></label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Subtitle</label>
                                        <input type="text" value={subPlanForm.homepage_subtitle} onChange={e => setSubPlanForm({...subPlanForm, homepage_subtitle: e.target.value})} placeholder="e.g. NEVER RUN OUT" className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" data-testid="hp-subtitle" /></div>
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Headline</label>
                                        <input type="text" value={subPlanForm.homepage_headline} onChange={e => setSubPlanForm({...subPlanForm, homepage_headline: e.target.value})} placeholder="e.g. QUARTERLY SUBSCRIPTION" className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" data-testid="hp-headline" /></div>
                                </div>
                                <div><label className="block text-white/40 text-xs uppercase mb-1">Description</label>
                                    <textarea value={subPlanForm.homepage_description} onChange={e => setSubPlanForm({...subPlanForm, homepage_description: e.target.value})} rows={2} className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm resize-none text-white placeholder:text-white/30" data-testid="hp-description" /></div>
                                <div><label className="block text-white/40 text-xs uppercase mb-1">Feature Bullets (3)</label>
                                    {subPlanForm.homepage_features.map((f, i) => (
                                        <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                                            <input type="text" value={f.title} placeholder={`Title ${i+1}`} onChange={e => { const fts = [...subPlanForm.homepage_features]; fts[i] = {...fts[i], title: e.target.value}; setSubPlanForm({...subPlanForm, homepage_features: fts}); }} className="bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" />
                                            <input type="text" value={f.description} placeholder={`Description ${i+1}`} onChange={e => { const fts = [...subPlanForm.homepage_features]; fts[i] = {...fts[i], description: e.target.value}; setSubPlanForm({...subPlanForm, homepage_features: fts}); }} className="bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" />
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">CTA Text</label>
                                        <input type="text" value={subPlanForm.homepage_cta_text} onChange={e => setSubPlanForm({...subPlanForm, homepage_cta_text: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" data-testid="hp-cta" /></div>
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">CTA Link</label>
                                        <input type="text" value={subPlanForm.homepage_cta_link} onChange={e => setSubPlanForm({...subPlanForm, homepage_cta_link: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" /></div>
                                </div>
                                <div><label className="block text-white/40 text-xs uppercase mb-1">Image</label>
                                    {subPlanForm.homepage_image_url ? (
                                        <div className="relative inline-block">
                                            <img src={normalizeRenderableImageUrl(subPlanForm.homepage_image_url)} alt="Subscription" className="w-32 h-32 object-cover border border-white/10" />
                                            <button type="button" onClick={() => setSubPlanForm({...subPlanForm, homepage_image_url: ''})} className="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs" data-testid="hp-image-remove">&times;</button>
                                        </div>
                                    ) : (
                                        <label className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/20 cursor-pointer hover:bg-white/10 transition-colors w-fit" data-testid="hp-image-upload-label">
                                            <Upload className="w-4 h-4 text-white/60" />
                                            <span className="text-white/60 text-sm">{uploading ? 'Uploading...' : 'Upload Image'}</span>
                                            <input type="file" accept="image/*" onChange={handleSubImageUpload} className="hidden" disabled={uploading} data-testid="hp-image-upload" />
                                        </label>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Badge Text</label>
                                        <input type="text" value={subPlanForm.homepage_badge_text} onChange={e => setSubPlanForm({...subPlanForm, homepage_badge_text: e.target.value})} placeholder="e.g. 15%" className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" /></div>
                                    <div><label className="block text-white/40 text-xs uppercase mb-1">Badge Description</label>
                                        <input type="text" value={subPlanForm.homepage_badge_description} onChange={e => setSubPlanForm({...subPlanForm, homepage_badge_description: e.target.value})} placeholder="e.g. Subscriber Savings" className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" /></div>
                                </div>
                            </div>
                        )}
                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsSubPlanModalOpen(false)} className="border-white/20">Cancel</Button>
                            <Button type="submit" className="bg-maple-red hover:bg-maple-red/80">{editingSubPlan ? 'Update' : 'Create'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Promo Code Modal */}
            <Dialog open={isPromoModalOpen} onOpenChange={setIsPromoModalOpen}>
                <DialogContent className="bg-maple-admin-card border-white/10 text-white max-w-lg">
                    <DialogHeader><DialogTitle className="font-anton text-2xl">{editingPromo ? 'Edit Promo' : 'Add Promo Code'}</DialogTitle></DialogHeader>
                    <form onSubmit={handlePromoSubmit} className="space-y-4">
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Code</label>
                            <input type="text" value={promoForm.code} onChange={e => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})} required disabled={!!editingPromo} className="w-full bg-maple-admin-bg border border-white/20 p-3 font-mono uppercase text-white placeholder:text-white/30" placeholder="SAVE20" /></div>
                        <div><label className="block text-white/40 text-xs uppercase mb-1">Description (optional)</label>
                            <input type="text" value={promoForm.description} onChange={e => setPromoForm({...promoForm, description: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" placeholder="Summer sale discount" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Discount Type</label>
                                <select value={promoForm.discount_type} onChange={e => setPromoForm({...promoForm, discount_type: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30">
                                    <option value="percentage">Percentage</option><option value="fixed">Fixed Amount</option>
                                </select></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">{promoForm.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}</label>
                                <input type="number" step="0.01" value={promoForm.discount_value} onChange={e => setPromoForm({...promoForm, discount_value: e.target.value})} required className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Min Order ($, optional)</label>
                                <input type="number" step="0.01" value={promoForm.min_order_amount} onChange={e => setPromoForm({...promoForm, min_order_amount: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Max Uses (optional)</label>
                                <input type="number" value={promoForm.max_uses} onChange={e => setPromoForm({...promoForm, max_uses: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" placeholder="Unlimited" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Expires (optional)</label>
                                <input type="date" value={promoForm.expires_at} onChange={e => setPromoForm({...promoForm, expires_at: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30" /></div>
                            <div><label className="block text-white/40 text-xs uppercase mb-1">Applies To</label>
                                <select value={promoForm.applies_to} onChange={e => setPromoForm({...promoForm, applies_to: e.target.value})} className="w-full bg-maple-admin-bg border border-white/20 p-3 text-white placeholder:text-white/30">
                                    <option value="all">All Orders</option><option value="products">Products Only</option><option value="subscriptions">Subscriptions Only</option>
                                </select></div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={promoForm.is_active} onChange={e => setPromoForm({...promoForm, is_active: e.target.checked})} className="w-4 h-4" /><span className="text-white/70 text-sm">Active</span></label>
                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsPromoModalOpen(false)} className="border-white/20">Cancel</Button>
                            <Button type="submit" className="bg-maple-red hover:bg-maple-red/80">{editingPromo ? 'Update' : 'Create'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Order Detail Modal */}
            <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
                <DialogContent className="bg-maple-admin-card border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selectedOrder && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center gap-3">
                                    <DialogTitle className="font-anton text-2xl">Order {selectedOrder.order_number || `#${selectedOrder.id.slice(0,8)}`}</DialogTitle>
                                    {selectedOrder.order_type === 'subscription' && (
                                        <span className="px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 uppercase">Subscription</span>
                                    )}
                                    {selectedOrder.customer_type === 'wholesale' && (
                                        <span className="px-2 py-0.5 text-[10px] bg-maple-red/20 text-maple-red uppercase">Wholesale</span>
                                    )}
                                </div>
                                <p className="text-white/30 text-xs mt-1">
                                    Created {new Date(selectedOrder.created_at).toLocaleString()}
                                </p>
                            </DialogHeader>
                            <div className="space-y-5">
                                {/* Status Controls */}
                                <div className="grid grid-cols-2 gap-4" data-testid="order-status-controls">
                                    <div>
                                        <p className="text-white/40 text-xs uppercase mb-1">Order Status</p>
                                        <select value={selectedOrder.status} onChange={e => updateOrderStatus(selectedOrder.id, 'status', e.target.value)}
                                            className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" data-testid="order-status-select">
                                            <option value="pending">Pending</option><option value="paid">Paid</option><option value="processing">Processing</option>
                                            <option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div>
                                        <p className="text-white/40 text-xs uppercase mb-1">Fulfillment</p>
                                        <select value={selectedOrder.fulfillment_status} onChange={e => updateOrderStatus(selectedOrder.id, 'fulfillment_status', e.target.value)}
                                            className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30" data-testid="order-fulfillment-select">
                                            <option value="unfulfilled">Unfulfilled</option><option value="partially_fulfilled">Partially Fulfilled</option><option value="fulfilled">Fulfilled</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Customer Info */}
                                <div data-testid="order-customer-info">
                                    <p className="text-white/40 text-xs uppercase mb-2">Customer</p>
                                    <div className="bg-maple-admin-bg border border-white/10 p-3">
                                        <p className="text-white font-medium">{selectedOrder.customer_name || 'N/A'}</p>
                                        <p className="text-white/60 text-sm">{selectedOrder.customer_email || 'No email'}</p>
                                        <p className="text-white/30 text-xs mt-2 uppercase tracking-wider">{selectedOrder.customer_type || 'retail'} customer</p>
                                        {selectedOrder.shipping_address && (
                                            <div className="mt-2 pt-2 border-t border-white/10 text-sm text-white/50">
                                                <p>{selectedOrder.shipping_address.address_line1}</p>
                                                {selectedOrder.shipping_address.address_line2 && <p>{selectedOrder.shipping_address.address_line2}</p>}
                                                <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postal_code}</p>
                                                <p>{selectedOrder.shipping_address.country}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Items */}
                                <div data-testid="order-items">
                                    <p className="text-white/40 text-xs uppercase mb-2">Items</p>
                                    <div className="space-y-1">
                                        {selectedOrder.items?.map((item, i) => (
                                            <div key={i} className="flex justify-between bg-maple-admin-bg p-3 border border-white/5">
                                                <div>
                                                    <span className="text-white">{item.quantity}x {item.product_name}</span>
                                                    {item.sku && <span className="text-white/30 text-xs ml-2">SKU: {item.sku}</span>}
                                                </div>
                                                <span className="text-white/60 font-mono">${(item.price * item.quantity).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Totals */}
                                <div className="border-t border-white/10 pt-4 space-y-2" data-testid="order-totals">
                                    <div className="flex justify-between text-sm"><span className="text-white/50">Subtotal</span><span className="font-mono text-white/70">${selectedOrder.subtotal?.toFixed(2) || selectedOrder.total?.toFixed(2)}</span></div>
                                    {selectedOrder.discount > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-white/50">Discount {selectedOrder.promo_code_used && <span className="text-green-400/70">({selectedOrder.promo_code_used})</span>}</span>
                                            <span className="font-mono text-green-400">-${selectedOrder.discount?.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg pt-1"><span className="font-anton text-white">TOTAL</span><span className="font-mono text-maple-red">${selectedOrder.total?.toFixed(2)}</span></div>
                                </div>

                                {/* Shipping & Tracking */}
                                <div className="border-t border-white/10 pt-4" data-testid="order-shipping">
                                    <p className="text-white/40 text-xs uppercase mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Shipping & Tracking</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Carrier</label>
                                            <select
                                                value={selectedOrder.tracking_carrier || ''}
                                                onChange={e => updateOrderStatus(selectedOrder.id, 'tracking_carrier', e.target.value)}
                                                className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm text-white placeholder:text-white/30"
                                                data-testid="order-carrier-select"
                                            >
                                                <option value="">Select carrier...</option>
                                                <option value="usps">USPS</option>
                                                <option value="ups">UPS</option>
                                                <option value="fedex">FedEx</option>
                                                <option value="dhl">DHL</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-white/40 text-xs uppercase mb-1">Tracking Number</label>
                                            <input type="text"
                                                defaultValue={selectedOrder.tracking_number || ''}
                                                onBlur={e => {
                                                    if (e.target.value !== (selectedOrder.tracking_number || '')) {
                                                        updateOrderStatus(selectedOrder.id, 'tracking_number', e.target.value);
                                                    }
                                                }}
                                                className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm font-mono text-white placeholder:text-white/30"
                                                placeholder="Enter tracking number"
                                                data-testid="order-tracking-input"
                                            />
                                        </div>
                                    </div>
                                    {selectedOrder.tracking_url && (
                                        <a href={selectedOrder.tracking_url} target="_blank" rel="noopener noreferrer"
                                            className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                            data-testid="order-tracking-link">
                                            <ExternalLink className="w-3.5 h-3.5" /> Track Package
                                        </a>
                                    )}
                                </div>

                                {/* Order Dates */}
                                <div className="border-t border-white/10 pt-4" data-testid="order-dates">
                                    <p className="text-white/40 text-xs uppercase mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Timeline</p>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                        <div className="flex justify-between"><span className="text-white/40">Created</span><span className="text-white/70 font-mono text-xs">{new Date(selectedOrder.created_at).toLocaleString()}</span></div>
                                        {selectedOrder.paid_at && <div className="flex justify-between"><span className="text-green-400/70">Paid</span><span className="text-white/70 font-mono text-xs">{new Date(selectedOrder.paid_at).toLocaleString()}</span></div>}
                                        {selectedOrder.shipped_at && <div className="flex justify-between"><span className="text-purple-400/70">Shipped</span><span className="text-white/70 font-mono text-xs">{new Date(selectedOrder.shipped_at).toLocaleString()}</span></div>}
                                        {selectedOrder.delivered_at && <div className="flex justify-between"><span className="text-green-500/70">Delivered</span><span className="text-white/70 font-mono text-xs">{new Date(selectedOrder.delivered_at).toLocaleString()}</span></div>}
                                    </div>
                                </div>

                                {/* Notes */}
                                <div className="border-t border-white/10 pt-4">
                                    <label className="block text-white/40 text-xs uppercase mb-1">Notes</label>
                                    <textarea
                                        defaultValue={selectedOrder.notes || ''}
                                        onBlur={e => {
                                            if (e.target.value !== (selectedOrder.notes || '')) {
                                                updateOrderStatus(selectedOrder.id, 'notes', e.target.value);
                                            }
                                        }}
                                        className="w-full bg-maple-admin-bg border border-white/20 p-2 text-sm resize-none text-white placeholder:text-white/30"
                                        rows={2}
                                        placeholder="Internal notes..."
                                        data-testid="order-notes-textarea"
                                    />
                                </div>

                                {/* Activity Log */}
                                <div className="border-t border-white/10 pt-4" data-testid="order-activity-log">
                                    <p className="text-white/40 text-xs uppercase mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Activity Log</p>
                                    {orderActivity.length === 0 ? (
                                        <p className="text-white/20 text-sm py-4 text-center">No activity recorded yet</p>
                                    ) : (
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {orderActivity.map((act, i) => (
                                                <div key={act.id || i} className="flex items-start gap-3 text-sm bg-maple-admin-bg p-2.5 border border-white/5">
                                                    <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                                                        act.action === 'status_change' ? 'bg-blue-400' :
                                                        act.action === 'fulfillment_change' ? 'bg-green-400' :
                                                        act.action === 'tracking_added' || act.action === 'shipped' ? 'bg-purple-400' :
                                                        'bg-white/30'
                                                    }`} />
                                                    <div className="flex-1 min-w-0">
                                                        {act.action === 'status_change' && (
                                                            <p className="text-white/70">Status changed from <span className="text-white/90 font-medium">{act.from}</span> to <span className="text-white/90 font-medium">{act.to}</span></p>
                                                        )}
                                                        {act.action === 'fulfillment_change' && (
                                                            <p className="text-white/70">Fulfillment changed from <span className="text-white/90 font-medium">{act.from}</span> to <span className="text-white/90 font-medium">{act.to}</span></p>
                                                        )}
                                                        {act.action === 'tracking_added' && (
                                                            <p className="text-white/70">Tracking added: <span className="font-mono text-white/90">{act.tracking_number}</span> via {act.carrier || 'unknown'}</p>
                                                        )}
                                                        {act.action === 'shipped' && (
                                                            <p className="text-white/70">Marked as shipped — <span className="font-mono text-white/90">{act.tracking_number}</span> ({act.tracking_carrier})</p>
                                                        )}
                                                        {!['status_change','fulfillment_change','tracking_added','shipped'].includes(act.action) && (
                                                            <p className="text-white/70">{act.action}</p>
                                                        )}
                                                        <p className="text-white/30 text-xs mt-0.5">{new Date(act.timestamp).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Customer Detail Modal */}
            <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
                <DialogContent className="bg-maple-admin-card border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selectedCustomer && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-anton text-2xl">{selectedCustomer.name || 'Customer'}</DialogTitle>
                                <p className="text-white/50 text-sm">{selectedCustomer.email}</p>
                            </DialogHeader>
                            <div className="space-y-5">
                                {/* Customer Stats */}
                                <div className="grid grid-cols-3 gap-4" data-testid="customer-stats">
                                    <div className="bg-maple-admin-bg border border-white/10 p-3 text-center">
                                        <p className="text-2xl font-mono text-white">{selectedCustomer.total_orders}</p>
                                        <p className="text-white/40 text-[10px] uppercase">Total Orders</p>
                                    </div>
                                    <div className="bg-maple-admin-bg border border-green-500/20 p-3 text-center">
                                        <p className="text-2xl font-mono text-green-400">${selectedCustomer.total_spent?.toFixed(2)}</p>
                                        <p className="text-white/40 text-[10px] uppercase">Total Spent</p>
                                    </div>
                                    <div className="bg-maple-admin-bg border border-white/10 p-3 text-center">
                                        <p className="text-sm font-mono text-white/70">{selectedCustomer.first_order ? new Date(selectedCustomer.first_order).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                                        <p className="text-white/40 text-[10px] uppercase">Customer Since</p>
                                    </div>
                                </div>

                                {/* Shipping Address */}
                                {selectedCustomer.shipping_address && (
                                    <div data-testid="customer-address">
                                        <p className="text-white/40 text-xs uppercase mb-2">Shipping Address</p>
                                        <div className="bg-maple-admin-bg border border-white/10 p-3 text-sm text-white/70">
                                            <p>{selectedCustomer.shipping_address.address_line1}</p>
                                            {selectedCustomer.shipping_address.address_line2 && <p>{selectedCustomer.shipping_address.address_line2}</p>}
                                            <p>{selectedCustomer.shipping_address.city}, {selectedCustomer.shipping_address.state} {selectedCustomer.shipping_address.postal_code}</p>
                                            <p>{selectedCustomer.shipping_address.country}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Active Subscriptions */}
                                {selectedCustomer.subscriptions?.length > 0 && (
                                    <div data-testid="customer-subscriptions">
                                        <p className="text-white/40 text-xs uppercase mb-2 flex items-center gap-2"><Repeat className="w-4 h-4" /> Subscriptions</p>
                                        <div className="space-y-2">
                                            {selectedCustomer.subscriptions.map((sub, i) => (
                                                <div key={sub.id || i} className="bg-maple-admin-bg border border-white/10 p-3 flex justify-between items-center">
                                                    <div>
                                                        <p className="text-white text-sm">{sub.plan_name || 'Subscription'}</p>
                                                        <p className="text-white/40 text-xs">{sub.interval} &middot; Started {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}</p>
                                                    </div>
                                                    <span className={`px-2 py-0.5 text-[10px] uppercase font-bold ${
                                                        sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                                        sub.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                                                        'bg-red-500/20 text-red-400'
                                                    }`}>{sub.status}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Order History */}
                                <div data-testid="customer-orders">
                                    <p className="text-white/40 text-xs uppercase mb-2 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Order History</p>
                                    {selectedCustomer.orders?.length === 0 ? (
                                        <p className="text-white/20 text-sm text-center py-6">No orders</p>
                                    ) : (
                                        <div className="space-y-2 max-h-72 overflow-y-auto">
                                            {selectedCustomer.orders?.map((order, i) => (
                                                <div key={order.id || i} className="bg-maple-admin-bg border border-white/5 p-3">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-mono text-white text-sm">{order.order_number || `#${(order.id || '').slice(0,8)}`}</span>
                                                                <span className={`px-2 py-0.5 text-[10px] uppercase ${
                                                                    order.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                                                                    order.status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                                                                    order.status === 'delivered' ? 'bg-green-600/20 text-green-500' :
                                                                    order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                                                    'bg-yellow-500/20 text-yellow-400'
                                                                }`}>{order.status}</span>
                                                                <span className={`px-2 py-0.5 text-[10px] uppercase ${
                                                                    order.fulfillment_status === 'fulfilled' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                                                }`}>{order.fulfillment_status}</span>
                                                                {order.order_type === 'subscription' && (
                                                                    <span className="px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 uppercase">Sub</span>
                                                                )}
                                                            </div>
                                                            <p className="text-white/40 text-xs mt-1">
                                                                {order.items?.length} item(s) &middot; {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </p>
                                                            <div className="text-white/30 text-xs mt-0.5">
                                                                {order.items?.map(item => `${item.quantity}x ${item.product_name}`).join(', ')}
                                                            </div>
                                                        </div>
                                                        <span className="font-mono text-maple-red ml-4">${order.total?.toFixed(2)}</span>
                                                    </div>
                                                    {order.tracking_number && (
                                                        <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-2 text-xs">
                                                            <Truck className="w-3 h-3 text-purple-400" />
                                                            <span className="text-white/50">{order.tracking_carrier?.toUpperCase()}</span>
                                                            <span className="font-mono text-white/70">{order.tracking_number}</span>
                                                            {order.tracking_url && (
                                                                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 ml-auto flex items-center gap-1">
                                                                    <ExternalLink className="w-3 h-3" /> Track
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;